import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import vi from "dayjs/locale/vi"
import { Send2 } from "iconsax-reactjs"
import { Loader2, RefreshCcwIcon, ReplyIcon } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"

import { Button } from "@/components/ui"
import { Image, Skeleton } from "@/components/ui"
import {
  createComment,
  getAllCommentByDraftId,
  getAllCommentByParentId,
  getDraftById
} from "@/features/jobs/manage/api/draft.api"
import { UpdateDraft } from "@/features/jobs/manage/components/working/update-draft"
import { EDraftStatus, EFolderAssets } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { TEXT_STATUS_DRAFT } from "@/modules/helpers/options"
import { getImageByNameFromS3 } from "@/modules/helpers/utils"
import { CommentDraft } from "@/modules/types"

type FormComment = {
  comment: string
  reply?: CommentDraft | null
}
// dayjs.extend(relativeTime)
dayjs.locale(vi)

interface DraftDetailProps {
  id: string
}

export function DraftDetail({ id }: DraftDetailProps) {
  const queryClient = useQueryClient()
  const { user } = useCurrentUser()
  const form = useForm<FormComment>({
    defaultValues: { comment: "", reply: null }
  })

  const {
    data: draft,
    isLoading,
    refetch: refetchDraft
  } = useQuery({
    queryKey: ["draft", id],
    queryFn: () => getDraftById(id),
    enabled: !!id
  })

  const {
    data: cmt,
    isFetching,
    refetch: refetchComments
  } = useQuery({
    queryKey: ["comments", id],
    queryFn: () => getAllCommentByDraftId(id),
    enabled: !!id
  })

  const { mutate: addComment, isPending: isPendingAddComment } = useMutation({
    mutationFn: createComment,
    onSuccess(_, raw) {
      form.reset()
      Promise.all([
        raw?.parentId &&
          queryClient.invalidateQueries({ queryKey: ["view_more_" + raw?.parentId] }),
        queryClient.invalidateQueries({ queryKey: ["comments", id] })
      ])
    }
  })

  const comments = useMemo(() => cmt?.data?.data?.list || [], [cmt])
  const draftDetails = useMemo(() => draft?.data?.data, [draft])

  const onAddComment = ({ comment, reply }: FormComment) => {
    if (!comment) return
    if (draftDetails?.id) {
      addComment({
        draftId: draftDetails.id?.toString(),
        content: comment,
        parentId: reply?.parentId || reply?.id
      })
    }
  }

  const onReply = (data?: CommentDraft) => {
    if (data?.id) {
      form.setValue("reply", data)
    }
  }

  return (
    <div className="flex flex-col">
      <UpdateDraft draft={draftDetails} refetch={refetchDraft} />
      <div className="flex flex-col h-fit overflow-y-auto gap-2">
        {/* Draft Info */}
        <div className="flex flex-col gap-2 mb-2">
          <p className="text-xs font-normal text-gray-900 whitespace-pre-wrap break-words">
            {draftDetails?.content}
          </p>
        </div>
        <div className="flex flex-col mb-3">
          {/* Video */}
          {isLoading ? (
            <Skeleton className="aspect-video" />
          ) : (
            <div className="w-full rounded-xl overflow-hidden border-dashed border border-gray-300 relative">
              <div
                className={cn(
                  "rounded-lg px-3 py-2 text-xs absolute top-2 z-10 left-2 text-white",
                  draftDetails?.status &&
                    [EDraftStatus.WAITING, EDraftStatus.DRAFT].includes(draftDetails?.status) &&
                    "border-gray-600",
                  draftDetails?.status &&
                    [
                      EDraftStatus.FINALIZED,
                      EDraftStatus.APPROVED,
                      EDraftStatus.ACCEPTED_ACCEPTANCE
                    ].includes(draftDetails?.status) &&
                    "bg-green-600",
                  draftDetails?.status &&
                    [EDraftStatus.REVIEWED, EDraftStatus.REJECTED_ACCEPTANCE].includes(
                      draftDetails?.status
                    ) &&
                    "bg-red-600",
                  draftDetails?.status === EDraftStatus.FINALIZED &&
                    draftDetails?.reason &&
                    "bg-orange-600"
                )}
              >
                {draftDetails?.status === EDraftStatus.FINALIZED && draftDetails?.reason
                  ? "Đang đợi nghiệm thu"
                  : draftDetails?.status
                    ? TEXT_STATUS_DRAFT[draftDetails?.status]
                    : "Chờ đăng"}
              </div>
              {draftDetails?.draftGalleries?.map((video: any, index: number) => (
                <video
                  key={`${video?.name}-${index}`}
                  controls
                  className="w-full aspect-video bg-black"
                  controlsList="nodownload"
                  playsInline
                >
                  <source
                    src={getImageByNameFromS3(video.name, user?.id, EFolderAssets.POTENTIAL_DRAFT)}
                    type="video/mp4"
                  />
                </video>
              ))}
            </div>
          )}
        </div>
        {/* Comment Section */}
        <div className="flex flex-col bg-white">
          {isFetching ? (
            <Skeleton className="w-full h-10 mb-2" />
          ) : comments.length > 0 ? (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-normal text-gray-900">
                  Bình luận{" "}
                  <span className="font-semibold text-primary-800">({comments.length})</span>
                </h3>
                <Button variant="ghost" className="text-xs" onClick={() => refetchComments()}>
                  <RefreshCcwIcon size={16} /> Làm mới
                </Button>
              </div>
              <div className="flex-1 mt-2 overflow-y-auto max-h-[500px]">
                {comments.map((comment) => (
                  <CommentDraftItem
                    key={comment.id}
                    onReply={onReply}
                    isReply={draft?.data?.data?.status === EDraftStatus.WAITING}
                    id={id as string}
                    data={comment}
                  />
                ))}
              </div>
            </>
          ) : (
            draft?.data?.data?.status === EDraftStatus.WAITING && (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-normal text-gray-900">
                    Bình luận{" "}
                    <span className="font-semibold text-primary-800">({comments.length})</span>
                  </h3>
                  <Button variant="ghost" className="text-xs" onClick={() => refetchComments()}>
                    <RefreshCcwIcon size={16} /> Làm mới
                  </Button>
                </div>
                <div className="flex-1 overflow-y-auto max-h-[500px]">
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <p className="text-xs text-gray-500">Chưa có bình luận</p>
                  </div>
                </div>
              </>
            )
          )}

          {draft?.data?.data?.status === EDraftStatus.WAITING && (
            <form onSubmit={form.handleSubmit(onAddComment)} className="py-3 px-1 bg-white">
              <Controller
                control={form.control}
                name="reply"
                render={({ field: { value, onChange } }) =>
                  value ? (
                    <div className="text-xs text-primary flex flex-col gap-1  ">
                      <div className="flex items-center justify-between ">
                        <p> Trả lời: </p>
                        <p className="text-xs text-primary-800" onClick={() => onChange(null)}>
                          Huỷ
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 bg-background border-l-2 border-primary p-2 mb-2 rounded-xs">
                        <p className="text-xs text-primary">
                          {`${value.createdUser?.username || ""}`}
                        </p>
                        <p className="text-xs text-primary-800">{value.content}</p>
                      </div>
                    </div>
                  ) : (
                    <></>
                  )
                }
              />

              <div className="flex items-start gap-2 ">
                <div
                  className={cn(
                    "flex-1 flex items-center gap-2 border border-primary p-1 rounded-full"
                  )}
                >
                  <div className="relative flex-1 flex gap-2 justify-center items-center">
                    <Controller
                      control={form.control}
                      name="comment"
                      render={({ field }) => (
                        <input
                          {...field}
                          placeholder="Viết bình luận..."
                          className="flex-1 text-xs p-4 !h-10 outline-none"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault()
                              if (field.value?.trim()) {
                                form.handleSubmit(onAddComment)()
                              }
                            }
                          }}
                        />
                      )}
                    />
                    <Button
                      type="submit"
                      size="icon"
                      disabled={isPendingAddComment || !form.watch("comment")?.trim()}
                      variant="ghost"
                      className="bg-gradient-primary rounded-full"
                    >
                      {isPendingAddComment ? (
                        <Loader2 className="animate-spin" size={16} />
                      ) : (
                        <Send2 size={18} color="#fff" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

interface BoxCommentItemProps {
  id?: string
  data?: CommentDraft
  onReply?: (data?: CommentDraft) => void
  isReply?: boolean
}

export const CommentDraftItem: React.FC<BoxCommentItemProps> = ({
  data,
  isReply = true,
  onReply
}) => {
  const [children, setChildren] = useState<CommentDraft[]>([])
  const [isViewMore, setIsViewMore] = useState<boolean>(false)
  const { data: comments } = useQuery({
    queryFn: () => getAllCommentByParentId(data?.id),
    enabled: !!data?.id,
    queryKey: ["view_more_" + data?.id]
  })

  const onViewMoreComment = () => {
    setIsViewMore(true)
  }
  useEffect(() => {
    if (comments?.data?.data?.list?.length) setChildren(comments?.data?.data?.list.reverse())
  }, [comments])

  return (
    <div className="w-full overflow-hidden mb-2">
      <div className="flex gap-2 w-full items-start">
        {/* Avatar - Facebook style */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
            <Image
              alt=""
              className="w-full h-full object-cover"
              width={24}
              height={24}
              src={getImageByNameFromS3(
                data?.createdUser?.avatarName,
                data?.createdUser?.id,
                EFolderAssets.AVATAR
              )}
            />
          </div>
        </div>

        {/* Comment Content - Facebook style */}
        <div className="flex-1 min-w-0">
          {/* Comment Bubble */}
          <div className="inline-block bg-[#F1F3F6] rounded-lg px-3 py-2 max-w-[85%]">
            <p className="text-xs font-semibold text-primary-800 mb-0.5">
              {data?.createdUser?.brandTaps?.name || data?.createdUser?.username}
            </p>
            <p className="text-xs tex-black leading-snug whitespace-pre-wrap break-words">
              {data?.content}
            </p>
          </div>

          {/* Action Buttons - Facebook style */}
          <div className="flex items-center gap-1 mt-1 ml-3">
            {isReply && (
              <button
                onClick={() => onReply?.(data)}
                className="text-xss font-semibold text-gray-600 hover:underline"
              >
                Trả lời
              </button>
            )}
            <span className="text-[10px] text-gray-500">
              {!!data?.createdAt && dayjs(data?.createdAt).fromNow()}
            </span>
          </div>
        </div>
      </div>
      {!!children?.length && isViewMore && (
        <div className="mt-3 ml-11 flex flex-col gap-3 relative">
          {children?.map((item) => (
            <div key={item?.id} className="relative pl-4">
              <CommentDraftItem data={item} onReply={onReply} />
            </div>
          ))}
        </div>
      )}

      {!isViewMore && Number(data?.totalComment) > 0 && (
        <div className="flex items-center ml-11 mt-2">
          <button
            onClick={() => onViewMoreComment()}
            className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors duration-200 group"
          >
            <div className="w-6 h-6 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors duration-200 flex items-center justify-center">
              <ReplyIcon size={14} className="rotate-180" />
            </div>
            <span>Xem {data?.totalComment || 0} phản hồi</span>
          </button>
        </div>
      )}
    </div>
  )
}
